/**
 * Membership 5787 — High Holiday seat model and pricing.
 *
 * A household's seats come in two clearly separate groups, and the form
 * mirrors that split:
 *
 *   1. INCLUDED seats — the allowance the membership already grants
 *      (2 for most tiers, 1 for Singles, 0 for First-Year Couples and for
 *      guests). Never charged. We still collect a name and gender for each
 *      one, because the office needs to know who is actually sitting in
 *      them for seating and for the printed seat cards.
 *
 *   2. ADDITIONAL seats — everyone beyond that allowance: guests, parents,
 *      siblings. Every one of these is charged.
 *
 * Keeping them as two separate lists is what stops the two from being
 * confused with each other. An earlier version collected a single
 * "number of men's seats / women's seats" pair and subtracted the
 * allowance from it, which under-charged real signups (a member couple
 * adding four guests was billed for two) and never captured who the
 * included seats belonged to.
 */
import {
  DEFAULT_INCLUDED_SEATS,
  DEFAULT_MEMBER_EXTRA_SEAT_PRICE,
  DEFAULT_NON_MEMBER_EXTRA_SEAT_PRICE,
} from '../config/membershipDefaults';

export const SEAT_GENDERS = ['male', 'female'];

export const emptySeatPerson = (gender = 'male') => ({ name: '', gender });

// Included seats default to one of each where the allowance allows it —
// the client's wording for the standard 2 is "husband + wife", so that is
// the sensible starting point rather than two blank male seats.
export const makeIncludedSeatPeople = (count) =>
  Array.from({ length: Math.max(0, Number(count) || 0) }, (_, i) =>
    emptySeatPerson(i === 1 ? 'female' : 'male')
  );

// The included-seat allowance changes when the visitor switches tier
// (Family 2 -> Singles 1 -> First-Year Couples 0), so the row count has to
// follow it without discarding anything already typed.
export const syncIncludedSeatPeople = (people = [], count) => {
  const target = Math.max(0, Number(count) || 0);
  const current = Array.isArray(people) ? people : [];
  if (current.length === target) return current;
  if (current.length > target) return current.slice(0, target);
  return [
    ...current,
    ...Array.from({ length: target - current.length }, (_, i) =>
      emptySeatPerson(current.length + i === 1 ? 'female' : 'male')
    ),
  ];
};

const isFilled = (person) => typeof person?.name === 'string' && person.name.trim().length > 0;

export const countSeatsByGender = (people = []) =>
  (Array.isArray(people) ? people : []).reduce(
    (acc, p) => {
      if (p?.gender === 'female') acc.female += 1;
      else acc.male += 1;
      return acc;
    },
    { male: 0, female: 0 }
  );

export const seatNamesByGender = (people = []) =>
  (Array.isArray(people) ? people : []).reduce(
    (acc, p) => {
      if (!isFilled(p)) return acc;
      const name = p.name.trim();
      if (p.gender === 'female') acc.womenSeatNames.push(name);
      else acc.menSeatNames.push(name);
      return acc;
    },
    { menSeatNames: [], womenSeatNames: [] }
  );

// Charged seats === additional seats. The allowance is reported alongside
// for display and is never deducted from what is charged.
export const computeSeatCost = ({
  additionalSeats = 0,
  includedSeats = DEFAULT_INCLUDED_SEATS,
  isMemberRate = true,
  extraSeatPrice,
} = {}) => {
  const extraSeats = Math.max(0, Number(additionalSeats) || 0);
  const allowance = isMemberRate ? Math.max(0, Number(includedSeats) || 0) : 0;
  const pricePerExtraSeat =
    extraSeatPrice != null
      ? Number(extraSeatPrice)
      : isMemberRate
        ? DEFAULT_MEMBER_EXTRA_SEAT_PRICE
        : DEFAULT_NON_MEMBER_EXTRA_SEAT_PRICE;

  return {
    totalSeats: allowance + extraSeats,
    includedSeats: allowance,
    extraSeats,
    pricePerExtraSeat,
    extraSeatCost: extraSeats * pricePerExtraSeat,
  };
};

// Every seat printed needs a name on it, so both lists are validated the
// same way. Errors are keyed per row so the field itself can show them.
export const validateSeatPeople = ({ includedSeatPeople = [], additionalSeatPeople = [] } = {}) => {
  const errors = { included: {}, additional: {} };

  includedSeatPeople.forEach((p, i) => {
    if (!isFilled(p)) errors.included[i] = 'Enter the name for this seat';
  });
  additionalSeatPeople.forEach((p, i) => {
    if (!isFilled(p)) errors.additional[i] = 'Enter the name for this seat';
  });

  const valid =
    Object.keys(errors.included).length === 0 && Object.keys(errors.additional).length === 0;
  return { valid, errors };
};

// Which rate/allowance applies. Any selected tier — including the free
// First-Year Couples tier — pays the member rate for additional seats; a
// guest with no tier gets no allowance and the non-member rate.
export const getSeatRateConfig = (tier, seatPricing = {}) => {
  const isMemberRate = Boolean(tier);
  return {
    isMemberRate,
    includedSeats: isMemberRate
      ? tier?.includedSeats ?? seatPricing.includedSeats ?? DEFAULT_INCLUDED_SEATS
      : 0,
    extraSeatPrice: isMemberRate
      ? seatPricing.memberExtraSeatPrice ?? DEFAULT_MEMBER_EXTRA_SEAT_PRICE
      : seatPricing.nonMemberExtraSeatPrice ?? DEFAULT_NON_MEMBER_EXTRA_SEAT_PRICE,
  };
};

// The non-member seat rate ($360) is set well above the member rate
// ($100) specifically so this gap is worth surfacing to a guest — used
// to show "members pay $X less per seat" and a running total once they've
// entered seats, without the guest ever needing to do the subtraction
// themselves.
export const computeMemberSeatSavings = (seatCount = 0, seatPricing = {}) => {
  const memberPrice = seatPricing.memberExtraSeatPrice ?? DEFAULT_MEMBER_EXTRA_SEAT_PRICE;
  const nonMemberPrice = seatPricing.nonMemberExtraSeatPrice ?? DEFAULT_NON_MEMBER_EXTRA_SEAT_PRICE;
  const perSeatSavings = Math.max(0, nonMemberPrice - memberPrice);
  const seats = Math.max(0, Number(seatCount) || 0);

  return {
    memberPrice,
    nonMemberPrice,
    perSeatSavings,
    totalSavings: perSeatSavings * seats,
  };
};
